import { TUXToast } from '@byted-tiktok/tux-components';
import { isCsr, isIOS, sendEvent } from '@fe/armor_utils';
import cls from 'classnames';
import copy from 'copy-to-clipboard';
import useConfig from 'packages/common/hooks/useConfig';
import useOpenView from 'packages/common/hooks/useOpenView';
import { formattingFn } from 'packages/common/number-format';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Props } from '.';
import Avatar from '../../common/components/Avatar';
import { getShareInfo, SharePageData } from '../share/service';
import { APPIDS, onelinkMap, querystringify } from '../share/util';
import DownloadBtn from './download-btn';
import CoinLeftOne from './images/coin-left-1.svg';
import CoinRightOne from './images/coin-right-1.svg';
import ImageMainBg from './images/image-main-bg.svg';
import VideoCloseSvg from './images/share-close-video.svg';
import ImageVideoPlaySvg from './images/share-image-video-play.svg';
import LiteLogoSvg from './images/share-logo-new-ui-lite.svg';
import LogoSvg from './images/share-logo-new-ui.svg';
import MoreInfoSvg from './images/share-more-icon.svg';
import VideoGroupPlaySvg from './images/share-video-group-play.svg';
import videoPoster from './images/video-poster.jpg';
import InviterCodeFooter from './inviter-code-footer';
import StepCard from './step-card';

const getOneLink = (query: Record<string, string>, code: string, _lng: string, region: string, aid: keyof typeof onelinkMap) => {
  const ACTIVITY_URL = `${window.location.origin}/activity/referral/task`;
  const config = onelinkMap[aid];
  const activityQuery: Record<string, string> = { ...query,
    enter_from: 'share',
    invite_code: code
  };

  if (region === 'br' && aid === '1340') {
    activityQuery.template = 'referral';
    activityQuery.cid = 'br_lite';
  }

  const schemaQuery = {
    url: `${ACTIVITY_URL}?${querystringify(activityQuery)}`
  };
  const onelinkQuery = {
    af_dp: `${config.schema}?${querystringify(schemaQuery)}`,
    af_web_dp: 'https://www.tiktok.com',
    c: `UG_Referral_${region.toUpperCase()}`,
    is_retargeting: 'true',
    pid: 'referral',
    af_adset: code
  };
  return `${config.link}?${querystringify(onelinkQuery)}`;
};

const format = (dataSource: SharePageData, query: Record<string, string>) => {
  const {
    invite_code,
    share_info,
    user_info
  } = dataSource;
  let title = '';
  let imageUrl = '';
  const {
    content_key,
    image_url_key
  } = query;

  if (share_info) {
    const {
      content_map,
      image_url_map
    } = share_info;

    if (content_map && typeof content_map[content_key] === 'string') {
      title = content_map[content_key];
    }

    if (image_url_map && typeof image_url_map[image_url_key] === 'string') {
      imageUrl = content_map[content_key];
    }
  }

  return {
    userName: user_info.name,
    userAvatar: user_info.avatar_url,
    inviteCode: invite_code,
    title,
    imageUrl
  };
};

const Module = (props: Props) => {
  const {
    ___commonParams,
    ___dataSource = []
  } = props;
  const dataSource = ___dataSource[0];
  const {
    t,
    config,
    isInApp
  } = useConfig();
  const {
    abDefault,
    newUserVideo: videoSrc = '',
    currencySymbol,
    currencyUnit,
    upto
  } = config;
  const query = ___commonParams.query;
  const isLite = query.aid === '1340';

  function getShareUIGroup() {
    // ABTest `invitation_opt_group` process:
    // 1. [task page / invite page] get `invitation_opt_group` from referral api
    // 2. [task page / invite page] pass `invitation_opt_group` as a query to the share ul
    // 3. [share page] get `invitation_opt_group` from url query
    if (query.referral_landing_opt_group) {
      return +query.referral_landing_opt_group;
    }

    if (abDefault?.abdefault_referral_landing_opt_group) {
      return +abDefault.abdefault_referral_landing_opt_group;
    }

    return 0;
  }

  let isImageGroup = getShareUIGroup() === 1;

  if (!isImageGroup && !videoSrc) {
    isImageGroup = true;
  }

  const {
    u_code,
    region,
    aid,
    lng,
    activity_name,
    enter_from
  } = query;
  const [shareInfo, setShareInfo] = useState(dataSource?.err_no === 0 ? format((dataSource.data as SharePageData), query) : {
    userName: '',
    userAvatar: '',
    inviteCode: '',
    title: '',
    imageUrl: ''
  });
  useEffect(() => {
    if (isCsr() && dataSource?.err_no !== 0) {
      getShareInfo({
        u_code,
        aid,
        carrier_region: region,
        activity_type: 2
      }).then(({
        data,
        err_no
      }) => {
        if (err_no === 0) {
          setShareInfo(format((data as SharePageData), query));
        }
      });
    }
  }, []);
  const {
    replaceTask
  } = useOpenView();
  useEffect(() => {
    if (isInApp) {
      if (shareInfo.inviteCode) {
        /**
         * share-page may be visited in TikTok App!
         *
         * Some share-page link format(e.g. https://vm.tiktok.com/ZSJPk6RRJ/) may trigger deeplink
         * (Android App Links / Apple Universal Links) and immediately send user to share-page within TikTok App.
         *
         * In such case, we can autoredirect to task-page once invite_code is ready
         * for (maybe) a better user experience :-)
         */
        replaceTask({
          invite_code: shareInfo.inviteCode
        });
      }
    }
  }, [isInApp, shareInfo.inviteCode]);
  useEffect(() => {
    sendEvent('luckycat_predefine_pageview_redpacket', {
      enter_from,
      from_user_id: u_code,
      region,
      activity_type: activity_name || 'invite',
      app_id: aid
    });
  }, [activity_name, aid, enter_from, region, u_code]);
  const handleCopy = useCallback(() => {
    copy(shareInfo.inviteCode);
    TUXToast.info(t('copy.success', 'Copied successfully'));
    sendEvent('luckycat_copy_invite_code', {
      from_user_id: query.u_code,
      is_succ: 1,
      enter_from: query.enter_from,
      activity_type: '',
      region: query.region,
      app_id: query.aid
    });
  }, [shareInfo.inviteCode]);
  const handleDownload = useCallback(() => {
    sendEvent('luckycat_click_download', {
      region: query.region,
      app_id: query.aid
    });
    let isLiteShareOnIOS = false;

    if (isIOS() && aid === '1340') {
      isLiteShareOnIOS = true;
    }

    location.href = getOneLink(query, shareInfo.inviteCode, lng, region, isLiteShareOnIOS ? APPIDS.M : (aid as keyof typeof onelinkMap));
  }, [shareInfo.inviteCode]);
  const playerDom = useRef<HTMLVideoElement>(null);
  const [isVideoFullScreen, setIsVideoFullScreen] = useState<boolean>(false);
  const [isVideoCanPlay, setIsVideoCanPlay] = useState<boolean>(false);
  const [isVideoPause, setIsVideoPause] = useState<boolean>(false);
  const cashFormattingFn = formattingFn('cash', currencySymbol, currencyUnit, query.lng);
  const steps = [{
    title: <section className="step-title">
          {t('referral_optimize_landing_step_01', 'Open TikTok')}
        </section>
  }, {
    title: <span>
          {isImageGroup ? t('referral_optimize_landing_step_02', "Enter {username}'s invite code", {
        username: shareInfo.userName
      }) : t('referral_optimize_enter_code', 'Enter invite code to earn {currencySymbol}{number}', {
        currencySymbol: cashFormattingFn(upto),
        number: ''
      })}
        </span>,
    extra: <section className={cls('copy-wrapper', 'mt-12')}>
          <section className={cls('copy', 'px-24', 'background-color-BGView')}>
            <span className={cls('code', 'H4-Regular', isImageGroup ? 'text-color-TextTertiary' : 'text-color-TextPrimary')}>
              {shareInfo.inviteCode}
            </span>
            <span className={cls('copy-button', 'H4-Bold')} onClick={handleCopy}>
              {t('copy', 'Copy')}
            </span>
          </section>
        </section>
  }];

  if (isImageGroup) {
    steps.push({
      title: <section className="step-title">
          {t('referral_optimize_landing_step_03', 'Get up to {currencySymbol}{number}', {
          currencySymbol: cashFormattingFn(upto),
          number: ''
        })}
        </section>
    });
  }

  const defaultContent: string = t('referral_optimize_landing_title', "Join TikTok using my invite code and we'll both get");
  const title = <>
      <p className={cls('text-center', 'my-0', isImageGroup ? 'H2-Bold' : 'video-group-text-title H4-SemiBold')}>
        {defaultContent}
      </p>
      <p className={cls('will-get-points', 'text-center', isImageGroup ? 'big-font' : 'small-font')}>
        {cashFormattingFn(upto)}
      </p>
    </>;

  const videoPauseHandler = () => {
    const video = playerDom.current;
    if (!video || isVideoPause) return;
    setIsVideoPause(true);
    video.pause();
  };

  const playVideoHandler = () => {
    sendEvent('luckycat_zoomvideo_click', {});
    const video = playerDom.current;
    if (!video || !isVideoCanPlay) return;
    const videoPlayPromise = video.play();

    if (videoPlayPromise !== undefined) {
      videoPlayPromise.then(_ => {
        if (!isVideoFullScreen) {
          setIsVideoFullScreen(true);
        }

        if (isVideoPause) {
          setIsVideoPause(false);
        }
      });
    } else {
      if (!isVideoFullScreen) {
        setIsVideoFullScreen(true);
      }

      if (isVideoPause) {
        setIsVideoPause(false);
      }
    }
  };

  const videoStopPlayingHandler = () => {
    const video = playerDom.current;
    if (!video) return;
    setIsVideoFullScreen(false);
    video.pause();
  };

  const inviteCodeFooterClickHandler: React.MouseEventHandler = useCallback(() => {
    sendEvent('luckycat_copyandgo_click', {
      from_user_id: query.u_code || '',
      is_succ: 1,
      enter_from: query.enter_from,
      activity_type: '',
      region: query.region,
      app_id: query.app_id
    });
    handleCopy();
    handleDownload();
  }, [handleCopy, handleDownload]);
  useEffect(() => {
    if (!playerDom.current) {
      return;
    }

    const callback = () => {
      setIsVideoCanPlay(true);
    };

    playerDom.current.addEventListener('canplay', callback, false);
    return () => {
      playerDom.current?.removeEventListener('canplay', callback);
    };
  }, [setIsVideoCanPlay]);
  return <div className={cls('container', isImageGroup ? 'px-16 image-group' : 'video-group', {
    'video-full-screen': isVideoFullScreen,
    'video-playing': !isVideoPause
  })}>
      {
      /* {styles} */
    }

      {isImageGroup ? <>
          <header className="page-header">
            <section>
              <img src={isLite ? LiteLogoSvg : LogoSvg} />
            </section>
            {videoSrc && <section className="video-play-wrapper H4-SemiBold text-color-ConstTextInverse" onClick={playVideoHandler}>
                <span>{t('referral_optimize_tutorial_watch_01', 'Watch')}</span>
                <img src={ImageVideoPlaySvg} className="play-icon" />
                <span>{t('referral_optimize_tutorial_watch_02', 'tutorial video')}</span>
                <img src={MoreInfoSvg} />
              </section>}
          </header>
          <section className="step-card-wrapper">
            <StepCard steps={steps} avatar={shareInfo.userAvatar} title={title} />
          </section>
        </> : <>
          <header className="page-header">
            <section className="text-wrapper">{title}</section>
            <section className="avatar-wrapper">
              <Avatar src={shareInfo.userAvatar} size={42} style={{
            background: 'none'
          }} />
            </section>
          </header>

          <section className="step-card-wrapper">
            <StepCard steps={steps} />
          </section>
        </>}

      <img src={VideoGroupPlaySvg} className="video-play-button" onClick={playVideoHandler} />

      <section className="video-player-wrapper">
        <video src={videoSrc} className="video-player" playsInline loop ref={playerDom} poster={videoPoster} onClick={videoPauseHandler} autoPlay />
      </section>

      <section className="video-close-icon" onClick={videoStopPlayingHandler} />

      <section className="download-btn-wrapper">
        <DownloadBtn btnClickHandler={handleDownload} shadowDomClassName={cls(isImageGroup ? 'image-group-btn-shadow' : 'video-group-btn-shadow')} />
      </section>

      <footer className="video-playing-inviter-footer">
        <InviterCodeFooter code={shareInfo.inviteCode} avatar={shareInfo.userAvatar} copyHandler={inviteCodeFooterClickHandler} />
      </footer>

      <style jsx>{`
        .avatar {
          display: block;
          border: 3px solid #ffffff !important;
        }
        .step-title {
          font-size: 17px;
          line-height: 30px;
          font-weight: 400;
        }
        .copy-wrapper {
          display: flex;
          justify-content: flex-end;
        }
        .copy {
          border-radius: 41px;
          width: 100%;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .copy-button {
          color: #9f0000;
        }

        .will-get-points {
          line-height: 38px;
          font-weight: bold;
          background-color: #fe2c55;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .will-get-points.big-font {
          background-image: linear-gradient(180deg, #fe2c55 13.59%, #ff4d58 94.24%);
          margin: 6px 0 18px;
          font-size: 40px;
        }
        .will-get-points.small-font {
          background-image: linear-gradient(180deg, #ff9f5a 0%, #fe5e2c 94.71%);
          margin: 0;
          font-size: 36px;
        }
        .image-group-btn-shadow {
          background: radial-gradient(72.21% 24.47% at 51.75% 72.34%, #ff3b57 0%, #bf2f41 100%);
          box-shadow: 0px 0px 13.7647px #d03347;
          border-radius: 33.8824px;
        }
        .video-group-btn-shadow {
          background: radial-gradient(72.21% 24.47% at 51.75% 72.34%, #ffa800 0%, #ff3d00 100%);
          box-shadow: 0px 0px 13.7647px #d03347;
          border-radius: 33.8824px;
        }
        .video-group-text-title {
          padding-top: 6px;
        }
      `}</style>

      <style jsx>{`
        .container {
          box-sizing: border-box;
          width: 100%;
          /* mobile viewport bug fix: https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/*/
          min-height: -webkit-fill-available;
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
        }
        .container.image-group {
          background: url(${ImageMainBg}) center 37px/402px auto no-repeat,
            radial-gradient(
              83.49% 105.95% at 78.4% -19.84%,
              rgba(255, 205, 78, 0.8) 0%,
              rgba(254, 44, 233, 0) 100%
            ),
            radial-gradient(
              109.22% 278.21% at 159.22% 125.47%,
              #25f4ee 10.94%,
              rgba(240, 37, 244, 0) 100%
            ),
            radial-gradient(
              43.2% 90.81% at -4.25% 124.06%,
              rgba(255, 205, 78, 0.8) 0%,
              rgba(255, 223, 106, 0) 100%
            ),
            linear-gradient(180deg, #fe2c55 0%, #f91844 100%), #161823;
          box-shadow: inset 0px 4px 169px rgba(255, 255, 255, 0.29);
          padding-top: 18px;
          padding-bottom: 137px;
        }

        .image-group .page-header {
          display: flex;
          justify-content: space-between;
        }

        .image-group .page-header .video-play-wrapper {
          display: flex;
          align-items: center;
        }
        .image-group .page-header .video-play-wrapper .play-icon {
          margin: 0 2px;
        }
        .image-group .step-card-wrapper {
          margin-top: 121px;
        }
        .image-group .video-player-wrapper {
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        .container.image-group .video-play-button {
          display: none;
        }

        .container.video-group {
          padding-bottom: 0;
        }
        .video-group .page-header {
          position: absolute;
          z-index: 2;
          width: 120%;
          top: 0;
          left: -10%;
          border-radius: 0 0 80% 80%;
          background: #f9f9f9;
        }
        .video-group .page-header .text-wrapper {
          width: 100vw;
          padding: 0 20px 20px;
          margin: 0 auto;
          background: url(${CoinLeftOne}) 0 2px/40px auto no-repeat,
            url(${CoinRightOne}) 100% -10px/53px auto no-repeat;
        }
        .video-group .avatar-wrapper {
          position: absolute;
          z-index: 2;
          padding: 2px;
          bottom: -24px;
          left: 50%;
          transform: translateX(-50%);
          border-radius: 50%;
          background: #fff;
        }
        .video-group .step-card-wrapper {
          bottom: 101px;
          left: 16px;
          right: 16px;
          position: absolute;
          z-index: 2;
        }
        .video-group .video-play-button {
          top: calc((100% - 409px + 130px) / 2);
        }

        .video-play-button {
          position: absolute;
          z-index: 2;
          top: 188px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
        }
        .video-player-wrapper {
          font-size: 0;
        }
        .video-player-wrapper .video-player {
          width: 100%;
          /* mobile viewport bug fix: https://allthingssmitty.com/2020/05/11/css-fix-for-100vh-in-mobile-webkit/*/
          min-height: -webkit-fill-available;
          min-height: 100vh;
          object-fit: cover;
          position: relative;
        }
        .video-close-icon {
          display: none;
          position: absolute;
          z-index: 2;
          top: 0;
          left: 0;
          width: 54px;
          height: 48px;
          background: url(${VideoCloseSvg}) center / 24px 24px no-repeat;
        }
        .download-btn-wrapper {
          position: fixed;
          z-index: 3;
          bottom: 20px;
          left: 0;
          right: 0;
          width: 100%;
        }
        .video-playing-inviter-footer {
          opacity: 0;
          transform: translateY(100%);
          transition: all 0.5s;
          position: fixed;
          z-index: 3;
          bottom: 0;
          left: 0;
          width: 100%;
        }

        .container .page-header,
        .container .avatar-wrapper,
        .container .play-icon,
        .container .step-card-wrapper,
        .container .download-btn-wrapper {
          transition: opacity 0.5s;
        }
        .container.video-full-screen {
          background: transparent;
        }
        .container.video-full-screen .page-header,
        .container.video-full-screen .avatar-wrapper,
        .container.video-full-screen .step-card-wrapper,
        .container.video-full-screen .download-btn-wrapper {
          opacity: 0;
        }
        .container.video-full-screen.video-playing .play-icon,
        .container.video-full-screen.video-playing .video-play-button {
          display: none;
        }
        .container.video-full-screen .video-play-button,
        .container.video-full-screen .video-close-icon {
          display: block;
        }
        .container.video-full-screen .video-playing-inviter-footer {
          opacity: 1;
          transform: translateY(0);
        }

        .container.video-full-screen .video-player-wrapper {
          position: relative;
          display: block;
          z-index: 2;
        }
        .container.image-group.video-full-screen .video-player-wrapper {
          position: absolute;
        }

        .container.video-full-screen .video-play-button {
          top: 50%;
          transform: none;
          margin: -69px;
          z-index: 3;
        }
      `}</style>
      <style jsx global>
        {`
          body,
          #root {
            background-color: ${isImageGroup ? '#fe2c55' : 'rgb(0, 0, 0)'} !important;
          }
        `}
      </style>
    </div>;
};

export default Module;